/**
 * Event Emitter Service
 * Provides a centralized event system for decoupling components and services.
 * Uses the native EventTarget API for modern event handling.
 */
class EventEmitterService extends EventTarget {
  /**
   * Emit a custom event with optional data
   * @param {string} eventName - The name of the event to emit
   * @param {*} data - Optional data to pass with the event
   */
  emit(eventName, data = null) {
    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: false,
      cancelable: true,
    });
    this.dispatchEvent(event);
  }

  /**
   * Subscribe to an event
   * @param {string} eventName - The name of the event to listen for
   * @param {Function} callback - The callback function to execute when the event is emitted
   * @param {Object} options - Options for the event listener
   */
  on(eventName, callback, options = {}) {
    this.addEventListener(eventName, callback, options);
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventName - The name of the event to stop listening for
   * @param {Function} callback - The callback function to remove
   * @param {Object} options - Options for removing the event listener
   */
  off(eventName, callback, options = {}) {
    this.removeEventListener(eventName, callback, options);
  }

  /**
   * Subscribe to an event once (automatically unsubscribes after first emission)
   * @param {string} eventName - The name of the event to listen for
   * @param {Function} callback - The callback function to execute when the event is emitted
   */
  once(eventName, callback) {
    const onceCallback = event => {
      callback(event);
      this.off(eventName, onceCallback);
    };
    this.on(eventName, onceCallback);
  }
}

const eventEmitter = new EventEmitterService();

export default eventEmitter;

export const EVENT_TYPES = {
  CONNECTION_LOST: 'connection_lost',
  RECONNECTING: 'reconnecting',
  RECONNECTED: 'reconnected',
  RECONNECTION_FAILED: 'reconnection_failed',
};
